import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './MisReferidos.css';
import MisReferidosSkeleton from './skeleton/MisReferidosSkeleton';

const MisReferidos = () => {
  const [referidos, setReferidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitationLink, setInvitationLink] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [myUsername, setMyUsername] = useState('');
  const [myReferralCode, setMyReferralCode] = useState('');

  // Referral Calculator State
  const [calcAmigos, setCalcAmigos] = useState(5);
  const [calcComprasPorAmigo, setCalcComprasPorAmigo] = useState(2);
  const [calcTipoCompra, setCalcTipoCompra] = useState('ticket_medio'); // 'ticket_basico', 'ticket_medio', 'ticket_vip', 'producto'

  // Leaderboard / Ranking State
  const [ranking, setRanking] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(true);

  // Points multiplier based on purchase type
  const PUNTOS_POR_TIPO = {
    ticket_basico: 4,      // Promedio cuarzo/obsidiana
    ticket_medio: 20,     // Promedio rubí/zafiro
    ticket_vip: 100,      // Promedio esmeralda/diamante
    producto: 5           // Promedio producto marketplace
  };

  const puntosMensualesEstimados = calcAmigos * calcComprasPorAmigo * (PUNTOS_POR_TIPO[calcTipoCompra] || 20);
  const puntosAnualesEstimados = puntosMensualesEstimados * 12;
  const sorteosEquivalentes = Math.max(1, Math.floor(puntosAnualesEstimados / 500));

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    const fetchReferidos = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          setLoading(false);
          return;
        }

        const { data: userData, error: userError } = await supabase
          .from('usuarios')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (userError || !userData) {
          setLoading(false);
          return;
        }

        setMyUsername(userData.username || 'Tú');
        setMyReferralCode(userData.codigo_referido || '');

        const baseInvitationLink = `https://matimarket.com/registrate?key_referido=${userData.codigo_referido}`;
        setInvitationLink(baseInvitationLink);

        const { data: referidosData, error: referidosError } = await supabase
          .from('referidos')
          .select('referido_codigo')
          .eq('referente_codigo', userData.codigo_referido);

        if (referidosError) {
          console.error('Error fetching referidos:', referidosError);
          setLoading(false);
          return;
        }

        const referidosInfo = [];
        for (const referido of (referidosData || [])) {
          const referidoCodigo = referido.referido_codigo;
          const { data: referidoUserData } = await supabase
            .from('usuarios')
            .select('email, fecha_registro, username')
            .eq('codigo_referido', referidoCodigo)
            .single();

          let maskedEmail = 'Usuario Registrado';
          let tiempoTranscurrido = 'Reciente';

          if (referidoUserData) {
            const email = referidoUserData.email || '';
            maskedEmail = email.length > 6 
              ? `${email.substring(0, 3)}******${email.substring(email.length - 10)}`
              : referidoUserData.username || 'Usuario';

            if (referidoUserData.fecha_registro) {
              tiempoTranscurrido = calcularTiempoTranscurrido(new Date(referidoUserData.fecha_registro));
            }
          }

          const { data: puntosData } = await supabase
            .from('tickets_compras')
            .select('puntos')
            .eq('usuario', userData.codigo_referido)
            .eq('referido', referidoCodigo)
            .eq('forma_pago', 'referido');

          const puntosTotales = (puntosData || []).reduce((total, compra) => total + (compra.puntos || 0), 0);

          referidosInfo.push({
            email: maskedEmail,
            fechaRegistro: tiempoTranscurrido,
            puntos: puntosTotales
          });
        }

        setReferidos(referidosInfo);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar los referidos:', error.message);
        setLoading(false);
      }
    };

    const fetchRanking = async () => {
      setLoadingRanking(true);
      try {
        // Try RPC first
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_ranking_referidos', { limit_count: 10 });

        if (!rpcError && rpcData && rpcData.length > 0) {
          setRanking(rpcData);
          setLoadingRanking(false);
          return;
        }

        // Fallback: Query referidos directly
        const { data: refRows } = await supabase
          .from('referidos')
          .select('referente_codigo');

        if (refRows && refRows.length > 0) {
          const counts = {};
          refRows.forEach(r => {
            if (r.referente_codigo) {
              counts[r.referente_codigo] = (counts[r.referente_codigo] || 0) + 1;
            }
          });

          const sortedCodes = Object.keys(counts)
            .sort((a, b) => counts[b] - counts[a])
            .slice(0, 10);

          const { data: usersData } = await supabase
            .from('usuarios')
            .select('codigo_referido, username')
            .in('codigo_referido', sortedCodes);

          const userMap = {};
          (usersData || []).forEach(u => {
            userMap[u.codigo_referido] = u.username;
          });

          const builtRanking = sortedCodes.map(code => ({
            codigo_referido: code,
            username: userMap[code] || `Usuario ${code.substring(0, 4)}`,
            cantidad_referidos: counts[code]
          }));

          setRanking(builtRanking);
        } else {
          // Fallback initial visual data if empty
          setRanking([
            { username: 'vamosvoy', cantidad_referidos: 9 },
            { username: 'mattbmx', cantidad_referidos: 3 },
            { username: 'matiasmedina', cantidad_referidos: 2 },
          ]);
        }
      } catch (err) {
        console.error('Error fetching ranking:', err);
      } finally {
        setLoadingRanking(false);
      }
    };

    fetchReferidos();
    fetchRanking();
  }, []);

  const calcularTiempoTranscurrido = (fecha) => {
    const ahora = new Date();
    const diferencia = ahora - fecha;
    const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));
    const meses = Math.floor(dias / 30);
    const años = Math.floor(meses / 12);

    if (años > 0) {
      return `Hace ${años} ${años === 1 ? 'año' : 'años'}`;
    } else if (meses > 0) {
      return `Hace ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
    } else {
      return `Hace ${Math.max(1, dias)} ${dias === 1 ? 'día' : 'días'}`;
    }
  };

  const copyToClipboard = async () => {
    if (!invitationLink) return;
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (error) {
      console.error('Error al copiar al portapapeles:', error.message);
    }
  };

  const shareViaWhatsApp = () => {
    if (!invitationLink) return;
    const text = `¡Hola! Únete a MatiMarket conmigo usando mi enlace oficial y gana puntos para participar en sorteos de iPhones, autos y premios increíbles: ${invitationLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) {
    return <MisReferidosSkeleton />;
  }

  return (
    <div className="referidos-page-container">
      {/* Hero Header */}
      <div className="referidos-hero">
        <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#F6E27A] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
          <span className="material-symbols-outlined text-sm">group_add</span>
          Programa Oficial de Embajadores MatiMarket
        </div>
        <h1 className="title-mm">Invita Amigos y Gana Puntos de por Vida</h1>
        <p className="referidos-hero-subtitle">
          Cada vez que un amigo registrado con tu código compre tickets o productos en MatiMarket, acumulas puntos automáticos para participar gratis en todos nuestros sorteos.
        </p>

        {/* Share Link Card */}
        {invitationLink ? (
          <div className="referidos-link-box">
            <span className="text-xs uppercase text-gray-400 font-semibold tracking-wider block mb-2">
              Tu Enlace Único de Referido
            </span>
            <div className="referidos-link-input-group">
              <input
                type="text"
                value={invitationLink}
                readOnly
                className="referidos-link-input"
              />
              <button
                type="button"
                className={`btn-copiar-link ${copiado ? 'copiado' : ''}`}
                onClick={copyToClipboard}
              >
                <span className="material-symbols-outlined text-base">
                  {copiado ? 'done_all' : 'content_copy'}
                </span>
                <span>{copiado ? '¡Copiado!' : 'Copiar'}</span>
              </button>
              <button
                type="button"
                className="btn-whatsapp-share"
                onClick={shareViaWhatsApp}
                title="Compartir por WhatsApp"
              >
                <span className="material-symbols-outlined text-base">share</span>
                <span className="hidden sm:inline">WhatsApp</span>
              </button>
            </div>
            {myReferralCode && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Tu código directo es: <span className="text-[#b7f900] font-mono font-bold text-sm">{myReferralCode}</span>
              </p>
            )}
          </div>
        ) : (
          <div className="p-4 bg-gray-800/60 rounded-xl border border-gray-700 max-w-lg mx-auto text-center mt-4">
            <p className="text-sm text-gray-300">Inicia sesión con tu cuenta para obtener tu enlace personalizado de referidos.</p>
          </div>
        )}
      </div>

      {/* Benefits Grid */}
      <section className="referidos-benefits-section">
        <h2 className="section-title-centered">
          ¿Por qué vale la pena referir en MatiMarket?
        </h2>
        <p className="section-subtitle-centered">
          Ganas más de lo que imaginas con un sistema diseñado para premiar tu comunidad.
        </p>

        <div className="benefits-grid">
          <div className="benefit-card">
            <div className="benefit-icon-wrapper bg-[#D4AF37]/15 text-[#D4AF37]">
              <span className="material-symbols-outlined">stars</span>
            </div>
            <h3 className="benefit-title">Puntos Automáticos</h3>
            <p className="benefit-desc">
              Ganas puntos en tiempo real cada vez que tus referidos adquieren tickets de sorteo o productos del catálogo.
            </p>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon-wrapper bg-[#b7f900]/15 text-[#b7f900]">
              <span className="material-symbols-outlined">card_giftcard</span>
            </div>
            <h3 className="benefit-title">Sorteos Gratuitos</h3>
            <p className="benefit-desc">
              Usa tus puntos ganados para participar por iPhones, PlayStation 5, herramientas y vehículos sin gastar dinero.
            </p>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon-wrapper bg-sky-500/15 text-sky-400">
              <span className="material-symbols-outlined">casino</span>
            </div>
            <h3 className="benefit-title">Multiplica en El Montoncito</h3>
            <p className="benefit-desc">
              Apuesta tus puntos ganados en nuestro juego interactivo de cartas y multiplica tu saldo para ganar aún más.
            </p>
          </div>

          <div className="benefit-card">
            <div className="benefit-icon-wrapper bg-purple-500/15 text-purple-400">
              <span className="material-symbols-outlined">all_inclusive</span>
            </div>
            <h3 className="benefit-title">Sin Límites de Amigos</h3>
            <p className="benefit-desc">
              Invita a 5, 20 o 100 personas. No hay límites en la cantidad de amigos ni en la vigencia de tus ganancias.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Calculator Section */}
      <section className="referidos-calculator-section">
        <div className="calculator-container">
          <div className="calculator-header">
            <div className="flex items-center gap-2 justify-center">
              <span className="material-symbols-outlined text-[#b7f900] text-3xl">calculate</span>
              <h2 className="text-2xl font-bold text-white">Calculadora de Ganancias por Referidos</h2>
            </div>
            <p className="text-sm text-gray-400 text-center max-w-lg mx-auto mt-1">
              Mueve los controles para simular cuántos puntos puedes ganar según la actividad de tu comunidad.
            </p>
          </div>

          <div className="calculator-body-grid">
            {/* Controls */}
            <div className="calculator-controls">
              {/* Slider: Amigos */}
              <div className="calc-control-group">
                <div className="flex justify-between items-center mb-1">
                  <label className="calc-label">Amigos o referidos activos:</label>
                  <span className="calc-badge-value">{calcAmigos} amigos</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={calcAmigos}
                  onChange={(e) => setCalcAmigos(parseInt(e.target.value, 10))}
                  className="calc-range-slider"
                />
                <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                  <span>1 amigo</span>
                  <span>25 amigos</span>
                  <span>50 amigos</span>
                </div>
              </div>

              {/* Slider: Compras por mes */}
              <div className="calc-control-group">
                <div className="flex justify-between items-center mb-1">
                  <label className="calc-label">Compras al mes por cada amigo:</label>
                  <span className="calc-badge-value">{calcComprasPorAmigo} compras/mes</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={calcComprasPorAmigo}
                  onChange={(e) => setCalcComprasPorAmigo(parseInt(e.target.value, 10))}
                  className="calc-range-slider"
                />
                <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                  <span>1 compra</span>
                  <span>5 compras</span>
                  <span>10 compras</span>
                </div>
              </div>

              {/* Selector: Tipo de compra */}
              <div className="calc-control-group">
                <label className="calc-label block mb-2">Tipo de compra habitual:</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className={`calc-type-btn ${calcTipoCompra === 'ticket_medio' ? 'active' : ''}`}
                    onClick={() => setCalcTipoCompra('ticket_medio')}
                  >
                    <span>🎟️ Tickets Rubí/Zafiro</span>
                    <small>~20 pts por compra</small>
                  </button>

                  <button
                    type="button"
                    className={`calc-type-btn ${calcTipoCompra === 'ticket_vip' ? 'active' : ''}`}
                    onClick={() => setCalcTipoCompra('ticket_vip')}
                  >
                    <span>💎 Tickets Esmeralda/Diamante</span>
                    <small>~100 pts por compra</small>
                  </button>

                  <button
                    type="button"
                    className={`calc-type-btn ${calcTipoCompra === 'ticket_basico' ? 'active' : ''}`}
                    onClick={() => setCalcTipoCompra('ticket_basico')}
                  >
                    <span>🎫 Tickets Cuarzo/Obsidiana</span>
                    <small>~4 pts por compra</small>
                  </button>

                  <button
                    type="button"
                    className={`calc-type-btn ${calcTipoCompra === 'producto' ? 'active' : ''}`}
                    onClick={() => setCalcTipoCompra('producto')}
                  >
                    <span>📦 Productos Marketplace</span>
                    <small>~5 pts por compra</small>
                  </button>
                </div>
              </div>
            </div>

            {/* Results Display */}
            <div className="calculator-results">
              <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold block mb-2 text-center">
                Ganancias Proyectadas
              </span>

              <div className="calc-result-box mb-4">
                <span className="text-xs text-gray-400">Puntos Estimados al Mes:</span>
                <div className="text-3xl font-black text-[#b7f900] tracking-tight">
                  +{puntosMensualesEstimados.toLocaleString()} <span className="text-lg font-bold">PTS</span>
                </div>
              </div>

              <div className="calc-result-box mb-4">
                <span className="text-xs text-gray-400">Proyección Anual (12 Meses):</span>
                <div className="text-2xl font-bold text-[#D4AF37]">
                  +{puntosAnualesEstimados.toLocaleString()} PTS
                </div>
              </div>

              <div className="calc-result-benefit">
                <span className="material-symbols-outlined text-[#b7f900] text-xl">redeem</span>
                <div className="text-xs text-gray-200">
                  ¡Equivale a aproximadamente <strong>{sorteosEquivalentes} participaciones completas</strong> en sorteos de alta gama al año <strong>totalmente GRATIS</strong>!
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard / Ranking Section */}
      <section className="referidos-ranking-section">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-[#b7f900]/10 border border-[#b7f900]/30 text-[#b7f900] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-2">
            <span className="material-symbols-outlined text-sm">leaderboard</span>
            Tabla de Honor MatiMarket
          </div>
          <h2 className="text-2xl font-bold text-white">Ranking de los Usuarios que Más Han Referido</h2>
          <p className="text-sm text-gray-400 max-w-lg mx-auto">
            Los líderes que están construyendo la mayor comunidad en MatiMarket. ¿Podrás superarlos?
          </p>
        </div>

        {loadingRanking ? (
          <div className="text-center py-10">
            <span className="loading loading-spinner loading-md text-[#b7f900]"></span>
            <p className="text-xs text-gray-400 mt-2">Cargando ranking de embajadores...</p>
          </div>
        ) : (
          <div className="ranking-table-card">
            <table className="ranking-table">
              <thead>
                <tr>
                  <th className="w-16 text-center">Puesto</th>
                  <th>Usuario</th>
                  <th>Rango de Embajador</th>
                  <th className="text-right">Referidos</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((user, idx) => {
                  const position = idx + 1;
                  let medalBadge = `#${position}`;
                  let rankTitle = 'Promotor';
                  let badgeClass = 'rank-bronze';

                  if (position === 1) {
                    medalBadge = '🥇';
                    rankTitle = 'Embajador Diamante';
                    badgeClass = 'rank-gold';
                  } else if (position === 2) {
                    medalBadge = '🥈';
                    rankTitle = 'Embajador Platino';
                    badgeClass = 'rank-silver';
                  } else if (position === 3) {
                    medalBadge = '🥉';
                    rankTitle = 'Líder Oro';
                    badgeClass = 'rank-bronze';
                  } else if (position <= 5) {
                    rankTitle = 'Promotor Estrella';
                    badgeClass = 'rank-star';
                  }

                  const isCurrentUser = myUsername && user.username === myUsername;

                  return (
                    <tr key={idx} className={isCurrentUser ? 'current-user-row' : ''}>
                      <td className="text-center font-bold text-base">
                        <span className="inline-block">{medalBadge}</span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center font-bold text-xs text-white">
                            {(user.username || 'U')[0].toUpperCase()}
                          </div>
                          <div>
                            <span className="font-semibold text-white block">
                              {user.username || `Usuario ${user.codigo_referido?.substring(0, 4) || ''}`}
                              {isCurrentUser && (
                                <span className="ml-2 text-[10px] bg-[#b7f900] text-black px-1.5 py-0.5 rounded font-bold">
                                  TÚ
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`rank-badge ${badgeClass}`}>
                          {rankTitle}
                        </span>
                      </td>
                      <td className="text-right font-bold text-[#b7f900] text-base">
                        {user.cantidad_referidos} <span className="text-xs text-gray-400 font-normal">amigos</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* User's Personal Referrals List */}
      <section className="referidos-personal-list-section">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-[#D4AF37]">history</span>
          Mis Referidos Registrados ({referidos.length})
        </h2>

        {referidos.length === 0 ? (
          <div className="p-8 text-center bg-gray-900/50 rounded-2xl border border-gray-800">
            <span className="material-symbols-outlined text-4xl text-gray-600 mb-2">person_add_disabled</span>
            <p className="text-gray-300 font-medium">Aún no tienes referidos registrados</p>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              Copia tu enlace arriba y compártelo con tus amigos o en redes sociales para empezar a sumar ganancias hoy mismo.
            </p>
          </div>
        ) : (
          <div className="personal-table-card">
            <table className="tabla-responsive referidos-table">
              <thead>
                <tr>
                  <th>Usuario / Email</th>
                  <th>Registrado</th>
                  <th className="text-right">Puntos Ganados</th>
                </tr>
              </thead>
              <tbody>
                {referidos.map((referido, index) => (
                  <tr key={index}>
                    <td className="font-medium text-gray-200">{referido.email}</td>
                    <td className="text-gray-400 text-sm">{referido.fechaRegistro}</td>
                    <td className="text-right font-bold text-[#b7f900]">+{referido.puntos.toLocaleString()} pts</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default MisReferidos;